// src/features/frighi/CryoCard.jsx
import React, { useState, useEffect } from "react";
import CryoChart from "./CryoChart";
import { useNavigate } from "react-router-dom";

function tipoLabel(value) {
  return String(value || "").toLowerCase() === "congelatore"
    ? "Congelatore"
    : "Frigo";
}

export default function CryoCard({ device }) {
  const [hasAlerted, setHasAlerted] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [historyDays, setHistoryDays] = useState(7);

  const navigate = useNavigate();

  const mac = device.mac ?? device.mac_address ?? device.macAddress ?? null;
  const macForLink = encodeURIComponent(mac || "");

  const minValue =
    device.limite_min === "" ||
    device.limite_min === null ||
    device.limite_min === undefined
      ? null
      : parseFloat(device.limite_min);
  const maxValue =
    device.limite_max === "" ||
    device.limite_max === null ||
    device.limite_max === undefined
      ? null
      : parseFloat(device.limite_max);
  const currentTemp =
    device.temp_ultima !== null && device.temp_ultima !== undefined
      ? parseFloat(device.temp_ultima)
      : null;

  const outOfRange =
    currentTemp != null &&
    ((minValue != null && currentTemp < minValue) ||
      (maxValue != null && currentTemp > maxValue));

  const isAlert = outOfRange || Number(device.alert_ultimo) === 1;

  const lastSeen = device.last_seen
    ? new Date(device.last_seen).toLocaleString("it-IT")
    : "-";

  useEffect(() => {
    if (outOfRange && !hasAlerted) {
      const name = device.nome || mac || `ID ${device.id}`;
      const tempStr = currentTemp != null ? `${currentTemp.toFixed(2)} C` : "n/d";

      alert(
        `ATTENZIONE!\nIl frigo "${name}" e fuori dai limiti.\nTemperatura attuale: ${tempStr}`
      );
      setHasAlerted(true);
    } else if (!outOfRange && hasAlerted) {
      setHasAlerted(false);
    }
  }, [outOfRange, hasAlerted, device.nome, mac, currentTemp, device.id]);

  return (
    <>
      <div
        className={`rounded-2xl p-4 border shadow transition-colors ${
          isAlert ? "bg-red-50 border-red-300" : "bg-white border-gray-200"
        }`}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="font-semibold">{device.nome || mac}</div>
          <span
            className={`text-xs px-2 py-1 rounded-full border ${
              isAlert
                ? "bg-red-100 border-red-300 text-red-800"
                : "bg-emerald-50 border-emerald-200 text-emerald-700"
            }`}
          >
            {isAlert ? "ALLARME" : "OK"}
          </span>
        </div>

        <div className="text-sm text-gray-500 mb-2">
          {mac} | Ultimo: {lastSeen}
        </div>

        <div className="flex flex-col gap-1 mb-2">
          <div className="flex items-baseline gap-3">
            <div className="text-3xl font-bold">
              {currentTemp != null ? currentTemp.toFixed(2) : "-"} C
            </div>
            <div className="text-xs text-gray-500">
              Limiti: min {minValue ?? "-"} | max {maxValue ?? "-"}
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Tipo impostato: {tipoLabel(device.tipo_conservazione)}
          </div>

          {outOfRange && (
            <div className="text-xs font-semibold text-red-700">
              Temperatura fuori dai limiti consentiti
            </div>
          )}
        </div>

        <CryoChart
          mac={mac}
          days={1}
          min={minValue}
          max={maxValue}
          height={140}
        />

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 border rounded-lg px-2 py-2"
            onClick={() => navigate(`/frighi/qr/${device.id}?mac=${macForLink}`)}
          >
            QR / Stampa
          </button>

          <button
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 border rounded-lg px-2 py-2"
            onClick={() =>
              navigate(`/frighi/manual/${device.id}?mac=${macForLink}`)
            }
          >
            Inserisci manuale
          </button>
        </div>

        <div className="mt-2">
          <button
            className="w-full bg-white hover:bg-gray-50 text-gray-900 border rounded-lg px-2 py-2"
            onClick={() => {
              setHistoryDays(7);
              setOpenHistory(true);
            }}
          >
            Storico
          </button>
        </div>
      </div>

      {openHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl border">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <div className="text-lg font-semibold">
                  Storico temperature - {device.nome || mac}
                </div>
                <div className="text-xs text-gray-500">{mac}</div>
              </div>

              <button
                className="px-3 py-2 rounded-lg hover:bg-gray-100"
                onClick={() => setOpenHistory(false)}
              >
                X
              </button>
            </div>

            <div className="p-4 flex gap-2">
              {[1, 7, 30].map((d) => (
                <button
                  key={d}
                  className={`px-4 py-2 rounded-lg border text-sm ${
                    historyDays === d ? "bg-gray-900 text-white" : "hover:bg-gray-50"
                  }`}
                  onClick={() => setHistoryDays(d)}
                >
                  {d === 1 ? "24h" : `${d} giorni`}
                </button>
              ))}
            </div>

            <div className="px-4 pb-6">
              <div className="h-80 rounded-xl border bg-white p-2">
                <CryoChart
                  mac={mac}
                  days={historyDays}
                  min={minValue}
                  max={maxValue}
                  height={320}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
